/**
 * tmsummary/utils.js — TM 요약 페이지 공통 유틸·스타일 상수
 *
 * 포함:
 *   - useIsMobile: 모바일 여부 감지 훅 (768px 기준)
 *   - 날짜 유틸: KST, nowKst, lastDayOf, monthRange, fmtDate
 *   - 실적 집계 헬퍼: getTotalTickets, getProductBreakdown
 *   - 차트 색상 팔레트: BAR_COLORS
 *   - 공통 스타일 상수: CARD, TH, TD, NAV_BTN, NAV_SELECT
 */
import { useEffect, useState } from 'react';
import { nowKST as _nowKST, daysInMonth as _daysInMonth } from '../../../utils/date';

// ── 모바일 감지 훅 ─────────────────────────────────────────────────────────────

/**
 * 화면 너비 768px 이하를 모바일로 감지하는 훅
 * resize 이벤트로 반응형 업데이트
 * @returns {boolean} 모바일 여부
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 날짜 유틸 ─────────────────────────────────────────────────────────────────

/** 한국 표준시 타임존 옵션 */
export const KST = { timeZone: 'Asia/Seoul' };

/**
 * 현재 KST 기준 Date 객체 반환
 * nowKST: utils/date.js 사용 (new Date 직접 사용 금지)
 * @returns {Date} KST 현재 시각
 */
export function nowKst() {
  return _nowKST();
}

/**
 * 지정 연월의 마지막 날짜 반환
 * daysInMonth: utils/date.js 사용 (new Date 직접 사용 금지)
 * @param {number} year  연도
 * @param {number} month 월 (1-12)
 * @returns {number} 마지막 날짜 (1-31)
 */
export function lastDayOf(year, month) {
  return _daysInMonth(year, month);
}

/**
 * 연월로 해당 월의 날짜 범위 반환
 * @param {number} year  연도
 * @param {number} month 월 (1-12)
 * @returns {{ start: string, end: string }} YYYY-MM-DD 형식 날짜 범위
 */
export function monthRange(year, month) {
  const mm   = String(month).padStart(2, '0');
  const last = String(lastDayOf(year, month)).padStart(2, '0');
  return { start: `${year}-${mm}-01`, end: `${year}-${mm}-${last}` };
}

/**
 * 날짜 문자열을 점 구분 형식으로 변환 (하이픈 → 점)
 * @param {string} v 날짜 문자열 (예: "2024-03-15")
 * @returns {string} 예: "2024.03.15"
 */
export function fmtDate(v) {
  if (!v) return '-';
  return String(v).replace(/-/g, '.');
}

// ── 실적 집계 헬퍼 ────────────────────────────────────────────────────────────

/**
 * 계약 콜 행 1건의 총 장수 계산
 * contract_items 배열 기반 (비서비스 항목만 합산)
 * @param {object} row 콜 데이터 행
 * @returns {number} 총 장수
 */
export function getTotalTickets(row) {
  if (!row.is_contracted) return 0;
  // contract_items 배열 기반 집계 (신규 포맷)
  const items = Array.isArray(row.contract_items) ? row.contract_items : [];
  if (items.length > 0) {
    return items
      .filter((i) => !i.isService)
      .reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  }
  // 하위 호환: 기존 ticket_count 필드 (구 데이터)
  const fallback = parseInt(row.ticket_count, 10);
  return isNaN(fallback) ? 0 : fallback;
}

/**
 * 계약 콜 배열에서 상품명별 장수를 집계하여 내림차순 반환
 * contract_items 배열 기반으로 상품별 합산
 * @param {object[]} calls 콜 데이터 배열
 * @returns {{ name: string, count: number }[]} 상품명별 장수 내림차순 배열
 */
export function getProductBreakdown(calls) {
  const map = {};
  (calls || []).forEach((row) => {
    if (!row.is_contracted) return;
    const items = Array.isArray(row.contract_items) ? row.contract_items : [];
    if (items.length > 0) {
      // contract_items 배열 기반 집계 (비서비스 항목만)
      items.filter((i) => !i.isService).forEach((i) => {
        const name = i.ticketTypeName || '?';
        const qty  = Number(i.quantity) || 0;
        if (qty > 0) map[name] = (map[name] || 0) + qty;
      });
    } else {
      // 하위 호환: ticket_count 기반 (구 데이터)
      const fallback = parseInt(row.ticket_count, 10);
      const label = row.ticket_type || '';
      if (!isNaN(fallback) && fallback > 0 && label) {
        map[label] = (map[label] || 0) + fallback;
      }
    }
  });
  return Object.entries(map)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * 신규 또는 재주문 계약만 필터링해 상품별 장수 집계
 * @param {object[]} calls 콜 데이터 배열
 * @param {boolean}  isReorder true=재주문만, false=신규만
 * @returns {{ name: string, count: number }[]}
 */
export function getProductBreakdownByType(calls, isReorder) {
  const filtered = (calls || []).filter((row) => Boolean(row.is_reorder) === isReorder);
  return getProductBreakdown(filtered);
}

/**
 * 파인그룹 총 장수 계산 (상품별 가중치 적용) — items 배열 입력
 * items: [{ name, count }] 형태 (API /tm/summary/kpi 응답 newItems+reorderItems 합산 입력)
 * 서버가 내려준 상품별 환산장수(weightedCount)를 우선 사용한다.
 * @param {object[]} items 상품별 장수 배열 [{ name, count }]
 * @returns {number} 가중 합산 총 장수
 */
export function getPineTotalFromItems(items) {
  return (items || []).reduce((sum, item) => {
    const count = Number(item.count || 0);
    const weight = Number(item.weight ?? 1);
    return sum + Number(item.weightedCount ?? item.weighted_count ?? (count * weight) ?? 0);
  }, 0);
}

/**
 * 외부 회사(DOT/LNJ 등) 총 장수 계산 — 모든 상품 수량 단순 합산
 * @param {object[]} items 상품별 장수 배열 [{ name, count }]
 * @returns {number} 단순 합산 총 장수
 */
export function getSimpleTotalFromItems(items) {
  return (items || []).reduce((sum, i) => sum + (Number(i.count) || 0), 0);
}

/**
 * 파인그룹 총 장수 계산 (상품별 가중치 적용) — calls 배열 입력 (달력용)
 * 서버가 내려준 상품별 환산장수(weightedQuantity)를 우선 사용한다.
 * @param {object[]} calls 콜 데이터 배열
 * @returns {number} 가중 합산 총 장수
 */
export function getPineTotal(calls) {
  return (calls || []).reduce((sum, row) => {
    if (!row.is_contracted) return sum;
    const items = Array.isArray(row.contract_items) ? row.contract_items : [];
    return sum + items
      .filter((i) => !i.isService)
      .reduce((itemSum, item) => {
        const qty = Number(item.quantity || 0);
        const weight = Number(item.weight ?? 1);
        return itemSum + Number(item.weightedQuantity ?? item.weighted_count ?? (qty * weight) ?? 0);
      }, 0);
  }, 0);
}

// ── 차트 색상 팔레트 ──────────────────────────────────────────────────────────

/** 가로 막대 차트 색상 순환 팔레트 */
export const BAR_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#3b82f6',
  '#ef4444', '#8b5cf6', '#06b6d4', '#f97316',
  '#84cc16', 'var(--text-muted)',
];

// ── 공통 스타일 상수 ──────────────────────────────────────────────────────────
// dark mode: CSS 변수 토큰 사용 (index.css .dark{} 에서 자동 전환)

/** 섹션 카드 */
export const CARD = {
  border: '1px solid var(--border)',
  borderRadius: 12,
  background: 'var(--bg-card)',
  boxShadow: 'none',
  padding: '16px 20px',
};

/** 테이블 헤더 셀 */
export const TH = {
  padding: '8px 12px',
  fontWeight: 600,
  fontSize: 12,
  color: 'var(--text-muted)',
  background: 'var(--bg-subtle)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  letterSpacing: '0.02em',
};

/** 테이블 데이터 셀 */
export const TD = {
  padding: '9px 12px',
  fontSize: 13,
  color: 'var(--text)',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

/** 월 이동 버튼 */
export const NAV_BTN = {
  padding: '5px 11px',
  fontSize: 14,
  border: '1px solid var(--border)',
  borderRadius: 7,
  cursor: 'pointer',
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  fontWeight: 600,
  lineHeight: 1,
};

/** 연/월 셀렉트 */
export const NAV_SELECT = {
  fontSize: 14,
  fontWeight: 600,
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: 7,
  color: 'var(--text)',
  background: 'var(--bg-card)',
  cursor: 'pointer',
};
